import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, CardHeader, CardBody, Modal, Row, Col, Spinner, Input, Button, Table, Badge, Alert,
  UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem,
  Nav, NavItem, NavLink, TabContent, TabPane,
} from 'reactstrap';
import { motion, AnimatePresence } from 'framer-motion';
import FeatherIcon from 'feather-icons-react';
import { useTranslation } from 'react-i18next';
import api from '../helpers/api';
import PurchaseOrderModal from './PurchaseOrderModal';
import ReceivePurchaseOrderModal from './ReceivePurchaseOrderModal';
import RegisterAssetsModal from './RegisterAssetsModal';

const STATUS_META = {
  DRAFT: { color: 'bg-secondary', icon: 'edit-3' },
  APPROVED: { color: 'bg-primary', icon: 'check' },
  SENT: { color: 'bg-info', icon: 'send' },
  ACKNOWLEDGED: { color: 'bg-info', icon: 'inbox' },
  PARTIALLY_DELIVERED: { color: 'bg-warning', icon: 'truck' },
  FULLY_DELIVERED: { color: 'bg-success', icon: 'check-circle' },
  CLOSED: { color: 'bg-success', icon: 'archive' },
  CANCELLED: { color: 'bg-danger', icon: 'x-circle' },
};


const PurchaseOrders = ({ locations = [], enumData = [], orgCurrency = '', defaultTaxRate = 0, initialPrefill = null, onPrefillConsumed }) => {
  const { t } = useTranslation();
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [count, setCount] = useState(20);
  const [createModal, setCreateModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const [detail, setDetail] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const [receiveModal, setReceiveModal] = useState(false);
  const [registerModal, setRegisterModal] = useState(false);
  const [registerLine, setRegisterLine] = useState(null);
  const [statusError, setStatusError] = useState('');
  const [receiveError, setReceiveError] = useState('');
  const [registerError, setRegisterError] = useState('');
  const [confirmDeliveredModal, setConfirmDeliveredModal] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [commentError, setCommentError] = useState('');
  const [detailTab, setDetailTab] = useState('details');

  const statusLabel = (s) => t(`planning_purchasing.postatus.${s.toLowerCase()}`,
    s.charAt(0) + s.slice(1).toLowerCase().replace(/_/g, ' '));

  // Closed POs also show their delivery outcome ("Closed – Delivered" / "Closed –
  // Partially delivered"). POs closed before the outcome existed show plain "Closed".
  const poStatusLabel = (po) => {
    if (po.status === 'CLOSED' && po.closedOutcome) {
      return po.closedOutcome === 'FULLY_DELIVERED'
        ? t('planning_purchasing.postatus.closed_delivered', 'Closed – Delivered')
        : t('planning_purchasing.postatus.closed_partially_delivered', 'Closed – Partially Delivered');
    }
    return statusLabel(po.status);
  };

  const fmtMoney = (v) => (Number(v) || 0).toFixed(2);

  const formatDate = (v) => {
    if (!v) return '-';
    const d = new Date(v); if (Number.isNaN(d.getTime())) return v;
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  };

  const formatDateTime = (v) => {
    if (!v) return '-';
    const d = new Date(v); if (Number.isNaN(d.getTime())) return v;
    return `${formatDate(v)} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  const fetchPos = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (statusFilter) p.append('status', statusFilter);
      if (search) p.append('search', search);
      p.append('count', count);
      const { data } = await api.get(`purchase-order/all?${p.toString()}`);
      setPos(data?.response || []);
    } catch (e) { console.error('PO list error', e); } finally { setLoading(false); }
  }, [statusFilter, search, count]);

  useEffect(() => { fetchPos(); }, [fetchPos]);

  // Open the create form pre-filled when an "Issue PO from request" prefill arrives.
  useEffect(() => {
    if (initialPrefill) {
      setEditData(null); setPrefill(initialPrefill); setApiError(''); setCreateModal(true);
      onPrefillConsumed?.();
    }
  }, [initialPrefill, onPrefillConsumed]);

  const refreshDetail = async (id) => {
    try { const { data } = await api.get(`purchase-order/get?id=${id}`); setDetail(data?.response || null); } catch (e) { console.error(e); }
  };

  const openDetail = async (po) => { setStatusError(''); setCommentText(''); setCommentError(''); setDetailTab('details'); setDetail(po); refreshDetail(po.id); };

  const handleComment = async () => {
    if (!commentText.trim()) return;
    setSubmittingComment(true); setCommentError('');
    try {
      await api.post('purchase-order/comment', { purchaseOrderId: detail.id, comment: commentText.trim() });
      setCommentText('');
      await refreshDetail(detail.id);
    } catch (e) { setCommentError(e?.response?.data?.message || t('shared.something_went_wrong', 'An error occurred')); }
    finally { setSubmittingComment(false); }
  };

  const handleSave = async (payload) => {
    setIsSubmitting(true); setApiError('');
    try {
      if (payload.id) await api.put('purchase-order/update', payload);
      else await api.post('purchase-order/create', payload);
      setCreateModal(false); setEditData(null); setPrefill(null);
      fetchPos();
    } catch (e) { setApiError(e?.response?.data?.message || t('shared.something_went_wrong', 'An error occurred')); }
    finally { setIsSubmitting(false); }
  };

  const handleStatus = async (status) => {
    setStatusError('');
    try {
      await api.put('purchase-order/update-status', { purchaseOrderId: detail.id, status });
      await refreshDetail(detail.id); fetchPos();
    } catch (e) { setStatusError(e?.response?.data?.message || t('shared.something_went_wrong', 'An error occurred')); }
  };

  const handleReceive = async (payload) => {
    setIsSubmitting(true);
    setReceiveError('');
    try { await api.post('purchase-order/receive', payload); setReceiveModal(false); await refreshDetail(detail.id); fetchPos(); }
    catch (e) { setReceiveError(e?.response?.data?.message || t('shared.something_went_wrong', 'An error occurred')); }
    finally { setIsSubmitting(false); }
  };

  const handleRegister = async (payload) => {
    setIsSubmitting(true);
    setRegisterError('');
    try { await api.post('purchase-order/register-assets', payload); setRegisterModal(false); setRegisterLine(null); await refreshDetail(detail.id); }
    catch (e) { setRegisterError(e?.response?.data?.message || t('shared.something_went_wrong', 'An error occurred')); }
    finally { setIsSubmitting(false); }
  };

  const openBlob = async (url, filename, newTab) => {
    try {
      const res = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: res.headers['content-type'] });
      const objUrl = URL.createObjectURL(blob);
      if (newTab) { window.open(objUrl, '_blank'); }
      else { const a = document.createElement('a'); a.href = objUrl; a.download = filename; a.click(); }
      setTimeout(() => URL.revokeObjectURL(objUrl), 10000);
    } catch (e) { console.error('blob error', e); alert(t('planning_purchasing.download_failed', 'Download failed')); }
  };

  const statusBadge = (po) => (
    <span className={`badge rounded-pill text-white text-uppercase ${STATUS_META[po.status]?.color || 'bg-secondary'}`}>
      <FeatherIcon icon={STATUS_META[po.status]?.icon || 'help-circle'} size={11} className="me-1" />{poStatusLabel(po)}
    </span>
  );

  // valid manual transitions from current status
  const nextActions = (s) => ({
    DRAFT: [['APPROVED', 'approve', 'success'], ['CANCELLED', 'cancel', 'danger']],
    APPROVED: [['SENT', 'mark_sent', 'primary'], ['CANCELLED', 'cancel', 'danger']],
    SENT: [['ACKNOWLEDGED', 'mark_acknowledged', 'info'], ['CANCELLED', 'cancel', 'danger']],
    ACKNOWLEDGED: [],
    PARTIALLY_DELIVERED: [['CLOSED', 'close', 'dark']],
    FULLY_DELIVERED: [['CLOSED', 'close', 'dark']],
  }[s] || []);

  const canReceive = (s) => ['SENT', 'ACKNOWLEDGED', 'PARTIALLY_DELIVERED'].includes(s);
  const canMarkDelivered = (s) => ['SENT', 'ACKNOWLEDGED', 'PARTIALLY_DELIVERED'].includes(s);
  const canEdit = (s) => ['DRAFT', 'APPROVED'].includes(s);

  return (
    <div>
      <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
        <button className="btn btn-success" onClick={() => { setEditData(null); setPrefill(null); setApiError(''); setCreateModal(true); }}>
          <FeatherIcon icon="plus" size={18} className="me-1" />{t('planning_purchasing.new_po', 'New Purchase Order')}
        </button>
        <button className="btn btn-outline-secondary" onClick={() => openBlob('purchase-order/export-csv', 'purchase_orders.csv', false)}>
          <FeatherIcon icon="download" size={16} className="me-1" />{t('planning_purchasing.export_csv', 'Export CSV')}
        </button>
        <select className="form-select ms-auto" style={{ maxWidth: 200 }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">{t('planning_purchasing.all_statuses', 'All statuses')}</option>
          {Object.keys(STATUS_META).map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
        </select>
        <Input type="text" style={{ maxWidth: 240 }} placeholder={t('planning_purchasing.search_po', 'Search POs...')} value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      <Card>
        <CardHeader><h3 className="card-title text-success mb-0">{t('planning_purchasing.purchase_orders', 'Purchase Orders')}</h3></CardHeader>
        <CardBody>
          <div className="table-responsive" style={{ maxHeight: 'calc(100vh - 340px)', overflowY: 'auto' }}>
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <th>#</th><th>{t('planning_purchasing.po_number', 'PO Number')}</th><th>{t('planning_purchasing.vendor', 'Vendor')}</th>
                  <th>{t('shared.status', 'Status')}</th><th className="text-end">{t('planning_purchasing.total', 'Total')}</th>
                  <th>{t('planning_purchasing.issue_date', 'Issue Date')}</th><th>{t('planning_purchasing.request', 'Request')}</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {pos.map((po, i) => (
                    <motion.tr key={po.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                      <th scope="row">{i + 1}</th>
                      <td><span className="text-primary hover-underline" style={{ cursor: 'pointer', fontWeight: 500 }} onClick={() => openDetail(po)}>{po.poNumber}</span></td>
                      <td>{po.vendorName || '-'}</td>
                      <td>{statusBadge(po)}</td>
                      <td className="text-end">{po.total != null ? `${fmtMoney(po.total)} ${po.currency || ''}` : '-'}</td>
                      <td>{formatDate(po.issueDate)}</td>
                      <td>{po.procurementRequestNumber || <span className="text-muted">-</span>}</td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
          {loading && <div className="text-center py-3"><Spinner color="primary" /></div>}
          {!loading && pos.length === 0 && (
            <div className="text-center py-5"><FeatherIcon icon="file-text" size={48} className="text-muted mb-3" /><p className="text-muted">{t('planning_purchasing.no_pos', 'No purchase orders found')}</p></div>
          )}
          {pos.length >= count && (
            <div className="text-center mt-3"><Button color="primary" onClick={() => setCount((c) => c + 20)}>{t('shared.load_more', 'Load More')}</Button></div>
          )}
        </CardBody>
      </Card>

      {/* Create / edit */}
      <PurchaseOrderModal isOpen={createModal} toggle={() => { setCreateModal(false); setEditData(null); setPrefill(null); }}
        poData={editData} prefill={prefill} onSubmit={handleSave} isSubmitting={isSubmitting} apiError={apiError}
        orgCurrency={orgCurrency} defaultTaxRate={defaultTaxRate} enumData={enumData} />

      {/* Detail */}
      <Modal isOpen={!!detail} toggle={() => setDetail(null)} size="xl">
        {detail && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="modal-content border-0">
            <div className="modal-header text-white d-flex justify-content-between align-items-center"
                 style={{ background: 'linear-gradient(135deg,#667eea,#764ba2)', padding: '1.25rem 1.5rem' }}>
              <div>
                <h4 className="text-white mb-1">{detail.poNumber} {statusBadge(detail)}</h4>
                <div className="small text-white">{detail.vendorName} &nbsp;·&nbsp; {formatDate(detail.issueDate)}
                  {detail.procurementRequestNumber && <> &nbsp;·&nbsp; {t('planning_purchasing.request', 'Request')}: {detail.procurementRequestNumber}</>}</div>
              </div>
              <div className="d-flex gap-2 align-items-center">
                <button className="btn btn-sm btn-light" title={t('planning_purchasing.pdf_preview', 'Preview PDF')}
                        onClick={() => openBlob(`purchase-order/pdf?id=${detail.id}`, `${detail.poNumber || 'purchase-order'}.pdf`, true)}>
                  <FeatherIcon icon="eye" size={14} className="me-1" />{t('planning_purchasing.pdf', 'PDF')}
                </button>
                <button className="btn btn-sm btn-light" title={t('planning_purchasing.pdf_download', 'Download PDF')}
                        onClick={() => openBlob(`purchase-order/pdf?id=${detail.id}`, `${detail.poNumber || 'purchase-order'}.pdf`, false)}>
                  <FeatherIcon icon="download" size={14} />
                </button>
                <button className="btn-close btn-close-white" onClick={() => setDetail(null)} />
              </div>
            </div>
            {/* Tab navigation — mirrors the Work Order detail modal */}
            <Nav tabs className="border-bottom-0 px-4 bg-light">
              <NavItem>
                <NavLink className={`nav-link-modern ${detailTab === 'details' ? 'active' : ''}`}
                         onClick={() => setDetailTab('details')}
                         style={{ border: 'none', borderRadius: '8px 8px 0 0', padding: '12px 20px', fontWeight: '500', transition: 'all 0.3s ease', cursor: 'pointer' }}>
                  <FeatherIcon icon="file-text" size={16} className="me-2" />
                  {t('shared.details', 'Details')}
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink className={`nav-link-modern ${detailTab === 'activity' ? 'active' : ''}`}
                         onClick={() => setDetailTab('activity')}
                         style={{ border: 'none', borderRadius: '8px 8px 0 0', padding: '12px 20px', fontWeight: '500', transition: 'all 0.3s ease', cursor: 'pointer' }}>
                  <FeatherIcon icon="activity" size={16} className="me-2" />
                  {t('shared.activity', 'Activity')}
                  {(detail.activity || []).length > 0 && (
                    <Badge color="secondary" pill className="ms-2" style={{ fontSize: '10px' }}>{detail.activity.length}</Badge>
                  )}
                </NavLink>
              </NavItem>
            </Nav>

            <div className="modal-body p-0" style={{ maxHeight: '65vh', overflow: 'auto' }}>
              {statusError && <Alert color="danger" className="mx-4 mt-3 mb-0">{statusError}</Alert>}
              <TabContent activeTab={detailTab}>
                <TabPane tabId="details">
                  <div className="p-4">
              <Row className="g-3 mb-3">
                <Col md={6}><div className="bg-light rounded p-3 h-100"><h6 className="text-primary">{t('planning_purchasing.vendor', 'Vendor')}</h6>
                  <div className="fw-medium">{detail.vendorName}</div><div className="small text-muted">{detail.vendorAddress}</div>
                  <div className="small text-muted">{detail.vendorContactEmail} {detail.vendorContactPhone}</div></div></Col>
                <Col md={6}><div className="bg-light rounded p-3 h-100"><h6 className="text-primary">{t('planning_purchasing.buyer', 'Buyer')}</h6>
                  <div className="fw-medium">{detail.buyerEntityName}</div><div className="small text-muted">{detail.buyerAddress}</div>
                  {detail.deliveryAddress && <div className="small mt-1"><strong>{t('planning_purchasing.delivery_address', 'Delivery')}:</strong> {detail.deliveryAddress}</div>}</div></Col>
              </Row>

              <div className="table-responsive mb-2">
                <Table size="sm" bordered className="align-middle">
                  <thead className="table-light"><tr>
                    <th>{t('shared.description', 'Description')}</th><th className="text-center">{t('planning_purchasing.qty', 'Qty')}</th>
                    <th className="text-center">{t('planning_purchasing.received', 'Received')}</th><th className="text-end">{t('planning_purchasing.unit_price', 'Unit Price')}</th>
                    <th className="text-end">{t('planning_purchasing.line_total', 'Total')}</th><th></th>
                  </tr></thead>
                  <tbody>
                    {(detail.lineItems || []).map((li) => (
                      <tr key={li.id}>
                        <td>{li.description || li.reference || '-'}{li.itemType !== 'FREE_TEXT' && (
                          <Badge color="light" className="ms-2 text-muted">
                            {li.itemType === 'ASSET'
                              ? t('planning_purchasing.type_asset', 'Asset')
                              : t('planning_purchasing.type_parts_consumables', 'Parts & Consumables')}
                          </Badge>
                        )}</td>
                        <td className="text-center">{li.quantity} {li.unit}</td>
                        <td className="text-center">{li.receivedQuantity ?? 0}/{li.quantity}</td>
                        <td className="text-end">{fmtMoney(li.unitPrice)}</td>
                        <td className="text-end">{fmtMoney(li.lineTotal)}</td>
                        <td className="text-center">
                          {li.itemType === 'ASSET' && (li.receivedQuantity ?? 0) > (li.registeredAssetCount ?? 0) && (
                            <button className="btn btn-sm btn-outline-primary py-0" onClick={() => { setRegisterLine(li); setRegisterModal(true); }}>
                              {t('planning_purchasing.register', 'Register')} ({li.registeredAssetCount ?? 0}/{li.receivedQuantity})
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              <Row className="mb-3"><Col md={{ size: 4, offset: 8 }}>
                <div className="d-flex justify-content-between"><span>{t('planning_purchasing.subtotal', 'Subtotal')}</span><span>{fmtMoney(detail.subtotal)} {detail.currency}</span></div>
                <div className="d-flex justify-content-between"><span>{t('planning_purchasing.tax', 'Tax')}</span><span>{fmtMoney(detail.taxAmount)} {detail.currency}</span></div>
                <div className="d-flex justify-content-between fw-bold border-top pt-1"><span>{t('planning_purchasing.total', 'Total')}</span><span>{fmtMoney(detail.total)} {detail.currency}</span></div>
              </Col></Row>

              {!!(detail.notes || '').trim() && (
                <div className="mb-3"><h6 className="text-primary">{t('shared.notes', 'Notes')}</h6>
                  <div className="small" style={{ whiteSpace: 'pre-wrap' }}>{detail.notes}</div>
                </div>
              )}

              {(detail.receipts || []).length > 0 && (
                <div className="mb-2"><h6 className="text-primary">{t('planning_purchasing.receipt_history', 'Receipt History')}</h6>
                  {detail.receipts.map((r) => (
                    <div key={r.id} className="small border-start ps-2 mb-1">
                      <FeatherIcon icon="package" size={12} className="me-1" />{formatDate(r.receivedAt)} — {r.receivedByName}
                      {' '}({(r.lines || []).map((l) => l.quantity).reduce((a, b) => a + b, 0)} {t('planning_purchasing.units', 'units')})
                      {r.notes && <span className="text-muted"> · {r.notes}</span>}
                    </div>
                  ))}
                </div>
              )}

                  </div>
                </TabPane>

                {/* Activity tab — mirrors the Work Order Activity tab (canonical pattern) */}
                <TabPane tabId="activity">
                  <div className="p-4">
                    <Card className="border-0 shadow-sm">
                      <CardBody className="p-4">
                <div className="d-flex align-items-center mb-4">
                  <div className="me-3">
                    <div className="bg-info bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                         style={{ width: 40, height: 40 }}>
                      <FeatherIcon icon="activity" size={20} className="text-info" />
                    </div>
                  </div>
                  <div>
                    <h5 className="mb-1 fw-bold">{t('shared.activity_timeline', 'Activity Timeline')}</h5>
                    <p className="text-muted small mb-0">{t('planning_purchasing.activity_timeline_sub', 'Complete history of this purchase order')}</p>
                  </div>
                </div>

                {(detail.activity || []).length > 0 ? (
                  <div className="activity-timeline" style={{ maxHeight: 300, overflowY: 'auto' }}>
                    {detail.activity.map((a, index) => (
                      <motion.div key={index} className="timeline-item d-flex mb-4"
                                  initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.05 }}>
                        <div className="timeline-marker me-3">
                          <div className="timeline-dot bg-primary rounded-circle d-flex align-items-center justify-content-center"
                               style={{ width: 32, height: 32, minWidth: 32 }}>
                            <FeatherIcon icon={a.eventType === 'COMMENT' ? 'message-circle' : 'clock'} size={14} className="text-white" />
                          </div>
                        </div>
                        <div className="timeline-content flex-grow-1">
                          <div className="timeline-card bg-light rounded p-3">
                            <div className="d-flex justify-content-between align-items-start">
                              <div>
                                <div className="fw-semibold text-dark">{a.actorName || t('planning_purchasing.deleted_user', 'Deleted user')}</div>
                                <div className="text-muted" style={a.eventType === 'COMMENT' ? { whiteSpace: 'pre-wrap' } : undefined}>
                                  {a.eventType === 'COMMENT'
                                    ? a.comment
                                    : t(`planning_purchasing.evt_${(a.eventType || '').toLowerCase()}`, (a.eventType || '').toLowerCase())}
                                </div>
                              </div>
                              <Badge color="light" className="text-muted small">
                                <FeatherIcon icon="calendar" size={12} className="me-1" />{formatDateTime(a.occurredAt)}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted">{t('shared.no_activity_recorded', 'No activity recorded')}</p>
                )}

                {/* Add Comment — WO pattern */}
                <div className="comment-section mt-4 pt-4 border-top">
                  <div className="d-flex align-items-center mb-3">
                    <div className="me-3">
                      <div className="bg-secondary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                           style={{ width: 32, height: 32 }}>
                        <FeatherIcon icon="message-circle" size={16} className="text-secondary" />
                      </div>
                    </div>
                    <h6 className="mb-0 fw-bold">{t('shared.add_comment', 'Add Comment')}</h6>
                  </div>
                  {commentError && <Alert color="danger">{commentError}</Alert>}
                  <div className="d-flex gap-3 align-items-start">
                    <div className="flex-grow-1">
                      <Input type="textarea" rows={2} className="border-0 shadow-sm"
                             style={{ resize: 'none', backgroundColor: '#f8f9fa', borderRadius: 12, padding: '12px 16px' }}
                             placeholder={t('planning_purchasing.add_comment_placeholder', 'Add a comment to this purchase order...')}
                             value={commentText} onChange={(e) => setCommentText(e.target.value)} disabled={submittingComment} />
                    </div>
                    <Button color="primary" size="sm" className="d-flex align-items-center gap-2" style={{ minWidth: 100 }}
                            disabled={!commentText.trim() || submittingComment} onClick={handleComment}>
                      {submittingComment
                        ? <><Spinner size="sm" />{t('shared.posting', 'Posting...')}</>
                        : <><FeatherIcon icon="send" size={14} />{t('shared.post', 'Post')}</>}
                    </Button>
                  </div>
                </div>
                      </CardBody>
                    </Card>
                  </div>
                </TabPane>
              </TabContent>
            </div>
            <div className="modal-footer bg-light">
              {/* Mirrors the consolidated request footer: one primary action, remaining
                  transitions under "Change Status", Close last. Receiving is the primary
                  whenever it applies; otherwise the first lifecycle transition is. */}
              {(() => {
                const transitions = nextActions(detail.status);
                const receivable = canReceive(detail.status);
                const primary = !receivable && transitions.length > 0 ? transitions[0] : null;
                const menuItems = receivable ? transitions : transitions.slice(1);
                const showMenu = menuItems.length > 0 || canMarkDelivered(detail.status);
                return (
                  <>
                    {canEdit(detail.status) && (
                      <button className="btn btn-outline-secondary" onClick={() => { setEditData(detail); setDetail(null); setApiError(''); setCreateModal(true); }}>
                        <FeatherIcon icon="edit" size={14} className="me-1" />{t('shared.edit', 'Edit')}
                      </button>
                    )}
                    {receivable && (
                      <button className="btn btn-success" onClick={() => { setReceiveError(''); setReceiveModal(true); }}>
                        <FeatherIcon icon="package" size={14} className="me-1" />{t('planning_purchasing.receive_items', 'Receive Items')}
                      </button>
                    )}
                    {primary && (
                      <button className={`btn btn-${primary[2]}`} onClick={() => handleStatus(primary[0])}>
                        {t(`planning_purchasing.action_${primary[1]}`, primary[1].replace(/_/g, ' '))}
                      </button>
                    )}
                    {showMenu && (
                      <UncontrolledDropdown direction="up">
                        <DropdownToggle color="outline-secondary" caret>
                          {t('shared.buttons_change_status', 'Change Status')}
                        </DropdownToggle>
                        <DropdownMenu>
                          {menuItems.map(([status, key]) => (
                            <DropdownItem key={status} onClick={() => handleStatus(status)}>
                              {t(`planning_purchasing.action_${key}`, key.replace(/_/g, ' '))}
                            </DropdownItem>
                          ))}
                          {canMarkDelivered(detail.status) && (
                            <DropdownItem onClick={() => setConfirmDeliveredModal(true)}>
                              {t('planning_purchasing.mark_delivered', 'Mark Fully Delivered')}
                            </DropdownItem>
                          )}
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    )}
                    <button className="btn btn-secondary" onClick={() => setDetail(null)}>{t('shared.close', 'Close')}</button>
                  </>
                );
              })()}
            </div>
          </motion.div>
        )}
      </Modal>

      <ReceivePurchaseOrderModal isOpen={receiveModal} toggle={() => setReceiveModal(false)} po={detail} onReceive={handleReceive} isSubmitting={isSubmitting} apiError={receiveError} />
      <RegisterAssetsModal isOpen={registerModal} toggle={() => { setRegisterModal(false); setRegisterLine(null); }} po={detail} line={registerLine} locations={locations} onRegister={handleRegister} isSubmitting={isSubmitting} apiError={registerError} />

      {/* Manual fully-delivered confirmation — it records no receipt, so stock stays untouched */}
      <Modal isOpen={confirmDeliveredModal} toggle={() => setConfirmDeliveredModal(false)} centered>
        <div className="modal-header">
          <h5 className="modal-title">{t('planning_purchasing.confirm_mark_delivered_title', 'Mark as fully delivered?')}</h5>
          <button type="button" className="btn-close" onClick={() => setConfirmDeliveredModal(false)} />
        </div>
        <div className="modal-body">
          <p className="mb-0">
            <FeatherIcon icon="alert-triangle" size={16} className="me-2 text-warning" />
            {t('planning_purchasing.confirm_mark_delivered_body',
              'This closes delivery without recording a receipt — stock quantities will not be updated. Use "Receive Items" if goods actually arrived.')}
          </p>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-outline-secondary" onClick={() => setConfirmDeliveredModal(false)}>
            {t('shared.cancel', 'Cancel')}
          </button>
          <button type="button" className="btn btn-success" onClick={() => { setConfirmDeliveredModal(false); handleStatus('FULLY_DELIVERED'); }}>
            {t('planning_purchasing.mark_delivered', 'Mark Fully Delivered')}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default PurchaseOrders;
